import { triggerCustomEvent } from 'lib/helpers';
import {
  outerHeight, insertAfter, detach, css,
} from 'lib/ez-query';

/**
Pure JS version of:
@license Sticky-kit v1.1.2 | WTFPL | Leaf Corcoran 2015 | http://leafo.net
 */

class StickyKit {
  constructor(target, opts) {
    const o = typeof opts !== 'undefined' ? opts : {};

    this.target = target;

    this.props = {
      enableBottoming: o.bottoming || true,
      innerScrolling: o.innerScrolling || true,
      manualSpacer: o.spacer || null,
      offsetTop: o.offsetTop || 0,
      parentSelector: o.parent || undefined,
      recalcEvery: o.recalcEvery || null,
      stickyClass: o.stickyClass || 'is_stuck',
      detached: o.detached || false,
      parentHeight: o.parentHeight || null,
    };

    this.lastScrollHeight = document.body.offsetHeight;
    this.fixed = false;
    this.bottomed = false;

    this.parent = this.getParent();

    this.spacer = this.props.manualSpacer !== null
      ? (this.props.manualSpacer && this.target.closest(this.props.manualSpacer))
      : this.createEmptyDiv();

    if (this.spacer) {
      css(this.spacer, 'position', css(this.target, 'position'));
    }

    this.recalc();

    if (this.height === this.props.parentHeight) {
      return;
    }

    this.lastPos = undefined;

    this.offset = this.props.offsetTop;

    this.recalcCounter = this.props.recalcEvery;

    window.addEventListener('touchmove', this.tick);
    window.addEventListener('scroll', this.tick);
    window.addEventListener('resize', this.recalcAndTick);

    document.body.addEventListener('StickyKit:recalc', this.recalcAndTick);

    this.target.addEventListener('StickyKit:detach', this.detach);

    setTimeout(this.tick, 0);
  }

  offsetTopFromDoc(elem) {
    let offsetTop = 0;
    let nextElem = elem;

    do {
      if (!Number.isNaN(nextElem.offsetTop)) {
        offsetTop += nextElem.offsetTop;
      }
      nextElem = nextElem.offsetParent;
    } while (nextElem.offsetParent);

    return offsetTop;
  }


  getParent() {
    let parent = this.target.parentNode;

    if (this.props.parentSelector !== null) {
      parent = this.target.closest(this.props.parentSelector);
    }

    if (!parent) {
      throw new Error('failed to find sticky parent');
    } else {
      return parent;
    }
  }

  recalc = () => {
    let restore;

    if (this.detached) {
      return;
    }

    this.lastScrollHeight = document.body.offsetHeight;

    this.borderTop = parseInt(css(this.parent, 'border-top-width'), 10) || 0;

    this.paddingTop = parseInt(css(this.parent, 'padding-top'), 10) || 0;

    this.paddingBottom = parseInt(css(this.parent, 'padding-bottom'), 10) || 0;

    this.parentTop = this.offsetTopFromDoc(this.parent) + this.borderTop + this.paddingTop;

    this.props.parentHeight = this.parent.offsetHeight;

    if (this.fixed) {
      this.fixed = false;
      this.bottomed = false;

      if (this.props.manualSpacer == null) {
        insertAfter(this.target, this.spacer);
        detach(this.spacer);
      }

      css(this.target, 'position', 'initial');
      css(this.target, 'top', 'initial');
      css(this.target, 'bottom', 'initial');

      this.target.classList.remove(this.props.stickyClass);

      restore = true;
    }

    this.top = this.offsetTopFromDoc(this.target) - (parseInt(css(this.target, 'margin-top'), 10) || 0) - this.props.offsetTop;

    this.height = outerHeight(this.target);

    this.elFloat = css(this.target, 'float');

    if (this.spacer) {
      css(this.spacer, 'width', `${this.target.outerWidth}px`);
      css(this.spacer, 'height', `${this.height}px`);
      css(this.spacer, 'display', css(this.target, 'display'));
      css(this.spacer, 'vertical-align', css(this.target, 'vertical-align'));
      css(this.spacer, 'float', this.elFloat);
    }

    if (restore) {
      this.tick();
    }
  }

  tick = () => {
    let delta;
    let recalced;
    let winHeight;
    let willBottom = null;

    if (this.detached) {
      return;
    }

    recalced = false;

    if (this.recalcCounter !== null) {
      this.recalcCounter -= 1;

      if (this.recalcCounter <= 0) {
        this.recalcCounter = this.props.recalcEvery;

        this.recalc();

        recalced = true;
      }
    }

    if (!recalced && document.body.offsetHeight !== this.lastScrollHeight) {
      this.recalc();

      recalced = true;
    }

    const scroll = window.pageYOffset;

    if (this.lastPos !== null) {
      delta = scroll - this.lastPos;
    }

    this.lastPos = scroll;

    if (this.fixed) {
      if (this.props.enableBottoming) {
        const thisHeight = scroll + this.height + this.offset;
        const parentHeight = this.props.parentHeight + this.parentTop;
        willBottom = thisHeight > parentHeight;

        if (this.bottomed && !willBottom) {
          this.bottomed = false;

          css(this.target, 'position', 'fixed');
          css(this.target, 'bottom', 'auto');
          css(this.target, 'top', `${this.offset}px`);

          triggerCustomEvent('StickyKit:unbottom');
        }
      }

      if (scroll < this.top) {
        this.fixed = false;

        this.offset = this.props.offsetTop;

        if (this.props.manualSpacer == null) {
          if (this.elFloat === 'left' || this.elFloat === 'right') {
            insertAfter(this.target, this.spacer);
          }

          detach(this.spacer);
        }

        css(this.target, 'position', 'initial');
        css(this.target, 'top', 'auto');
        this.target.classList.remove(this.props.stickyClass);

        triggerCustomEvent('StickyKit:unstick', {}, this.target);
      }

      if (this.props.innerScrolling) {
        winHeight = window.offsetHeight;

        if (this.height + this.props.offsetTop > winHeight) {
          if (!this.bottomed) {
            this.offset -= delta;
            this.offset = Math.max(winHeight - this.height, this.offset);
            this.offset = Math.min(this.props.offsetTop, this.offset);

            if (this.fixed) {
              css(this.target, 'top', `${this.offset}px`);
            }
          }
        }
      }
    } else if (scroll > this.top) {
      this.fixed = true;

      css(this.target, 'position', 'fixed');
      css(this.target, 'top', `${this.offset}px`);

      this.target.classList.add(this.props.stickyClass);

      if (this.props.manualSpacer == null) {
        insertAfter(this.spacer, this.target);

        if (this.elFloat === 'left' || this.elFloat === 'right') {
          this.spacer.appendChild(this.target);
        }
      }

      triggerCustomEvent('StickyKit:stick', {}, this.target);
    }

    if (this.fixed && this.props.enableBottoming) {
      if (willBottom === null) {
        const thisHeight = scroll + this.height + this.offset;
        const parentHeight = this.props.parentHeight + this.parentTop;
        willBottom = thisHeight > parentHeight;
      }

      if (!this.bottomed && willBottom) {
        this.bottomed = true;

        if (css(this.parent, 'position') === 'static') {
          css(this.parent, 'position', 'relative');
        }

        css(this.target, 'position', 'absolute');
        css(this.target, 'bottom', `${this.paddingBottom}px`);
        css(this.target, 'top', 'auto');

        triggerCustomEvent('StickyKit:bottom', this.target);
      }
    }
  }

  recalcAndTick = () => {
    this.recalc();

    return this.tick();
  }

  detach = () => {
    this.detached = true;

    window.removeEventListener('touchmove', this.tick);
    window.removeEventListener('scroll', this.tick);
    window.removeEventListener('resize', this.recalcAndTick);

    document.body.removeEventListener('StickyKit:recalc', this.recalcAndTick);

    this.target.removeEventListener('StickyKit:detach', this.detach);

    css(this.target, 'position', 'initial');
    css(this.target, 'bottom', 'initial');
    css(this.target, 'top', 'initial');

    if (this.fixed) {
      if (this.props.manualSpacer == null) {
        if (this.elFloat === 'left' || this.elFloat === 'right') {
          insertAfter(this.target, this.spacer);
        }

        this.spacer.parentNode.removeChild(this.spacer);
      }

      return this.target.classList.remove(this.props.stickyClass);
    }
    return false;
  }

  createEmptyDiv() {
    const div = document.createElement('div');

    div.innerHTML = '';

    return div;
  }
}

export default function stickykit(target, opts) {
  return new StickyKit(target, opts);
}
